import { UpdateCart } from '@diamond/shared/types';
import { patchCart } from '@diamond/sol-buyer-context';
import { useAuthStore, useCartStore } from '@diamond/sol-buyer-context';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { patchCartItemInCartQueries } from './cartQueryCache';

export interface UseUpdateCartNoteArgs {
  itemId: string;
  getCurrentQuantityMagnified: () => number;
}

export interface UseUpdateCartNoteReturn {
  handleUpdateNote: (note: string) => void;
  isUpdating: boolean;
}

export const useUpdateCartNote = ({
  itemId,
  getCurrentQuantityMagnified,
}: UseUpdateCartNoteArgs): UseUpdateCartNoteReturn => {
  const token = useAuthStore((state) => state.access_token);
  const updateCartItemNote = useCartStore((state) => state.updateCartItemNote);
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (params: UpdateCart) => patchCart(token, itemId, params),
    onSuccess: (_data, variables) => {
      const noteText = variables.note ? String(variables.note) : '';
      updateCartItemNote(itemId, noteText);
      patchCartItemInCartQueries(queryClient, itemId, {
        quantity: variables.quantity,
        note: variables.note,
      });
    },
  });

  const handleUpdateNote = useCallback(
    (note: string) => {
      const isEmpty = note === '' || note == null;
      if (isEmpty) {
        updateCartItemNote(itemId, '');
        patchCartItemInCartQueries(queryClient, itemId, { note: '' });
        return;
      }
      const currentQuantity = getCurrentQuantityMagnified();
      const updatePayload: UpdateCart = {
        quantity: currentQuantity,
        note: note || null,
      };
      mutate(updatePayload);
    },
    [
      mutate,
      getCurrentQuantityMagnified,
      itemId,
      queryClient,
      updateCartItemNote,
    ]
  );

  return {
    handleUpdateNote,
    isUpdating: isPending,
  };
};
